import { match, P } from 'ts-pattern'
import type { HttpClient } from '@wix/http-client'
import type { PageRef } from '@wix/editor-platform-sdk-types'
import type {
  DynamicPageRouterData,
  EditorSDK,
} from '@wix/wix-data-client-common-standalone'
import type { RouterConfig } from '@wix/wix-data-client-common'
import { getPatternDefForPage } from '@wix/wix-data-client-common'
import { getPageRouter } from '../utils/getPageRouter'
import { createSitemapCountApi } from './sitemapCountApi'
import type { SitemapCountApi } from './sitemapCountApi'
import type {
  DynamicPageUrlCounter,
  ResponseData,
  SuccessResponseData,
  SuccessResult,
} from './types'
import { mapValues } from 'lodash'
import { getRoutersByPageRefs } from '../utils/getRoutersByPageRefs'

const successfullResponse = (
  responseData: ResponseData,
): responseData is SuccessResponseData => !responseData.exception

interface DynamicPageUrlCountsState {
  [pageId: string]: PageState | undefined
}

type PageState = { type: 'ready'; count: number } | { type: 'error' }

interface UrlCounterReadyState {
  type: 'ready'
  dynamicPageUrlCounts: DynamicPageUrlCountsState
}
type UrlCounterState =
  | {
      type: 'loading'
      promise: Promise<void>
      dynamicPageUrlCounts?: DynamicPageUrlCountsState
    }
  | { type: 'error' }
  | UrlCounterReadyState

const fetchPageUrlCount = async (
  editorSdkProxy: EditorSDK,
  sitemapCountApi: SitemapCountApi,
  pageRef: PageRef,
): Promise<PageState> => {
  const router = await getPageRouter({
    editorSdkProxy,
    pageRef,
  })

  if (!router) {
    return { type: 'error' }
  }

  const patternDefinition = getPatternDefForPage(
    router.config,
    router.pages,
    pageRef,
  )

  if (!patternDefinition) {
    return { type: 'error' }
  }

  const [response] = await sitemapCountApi.fetchRoutersPatternCounts([router])

  if (successfullResponse(response)) {
    const count = response.result.find(
      entry => entry.pageName === pageRef.id,
    )?.count
    if (count !== undefined) {
      return {
        type: 'ready',
        count,
      }
    }
  }
  return { type: 'error' }
}

const fetchPageUrlCounts = async (
  sitemapCountApi: SitemapCountApi,
  routers: DynamicPageRouterData[],
): Promise<DynamicPageUrlCountsState> => {
  const responses: Array<SuccessResult> = (
    await sitemapCountApi.fetchRoutersPatternCounts(routers)
  )
    .filter(successfullResponse)
    .map(responseData => responseData.result)

  return responses.flat().reduce(
    (allCounts, pageCount) => ({
      ...allCounts,
      [pageCount.pageName]: {
        type: 'ready' as const,
        count: pageCount.count,
      },
    }),
    {} as DynamicPageUrlCountsState,
  )
}

const getCountFromPageState = (pageState: PageState): number | null =>
  match(pageState)
    .with({ type: 'ready' }, result => result.count)
    .otherwise(() => null)

export const createDynamicPageUrlCounter = ({
  editorSdkProxy,
  httpClient,
  getGridAppId,
}: {
  editorSdkProxy: EditorSDK
  httpClient: HttpClient
  getGridAppId: () => Promise<string | undefined>
}): DynamicPageUrlCounter => {
  const sitemapCountApi = createSitemapCountApi(httpClient, getGridAppId)

  let state: UrlCounterState

  const fetchCountsSafely = async (routers: DynamicPageRouterData[]) =>
    fetchPageUrlCounts(sitemapCountApi, routers)
      .then(dynamicPageUrlCounts => ({
        type: 'ready' as const,
        dynamicPageUrlCounts,
      }))
      .catch(() => ({
        type: 'error' as const,
      }))

  const fetchAllPageUrlCountsSafely = async () => {
    const routers = await editorSdkProxy.routers.getAll<RouterConfig>()
    return fetchCountsSafely(routers)
  }

  const fetchPageUrlCountsSafely = async (pageRefs: PageRef[]) => {
    const routers = await getRoutersByPageRefs({ editorSdkProxy, pageRefs })
    return fetchCountsSafely(routers)
  }

  const fetchPageUrlCountSafely = (pageRef: PageRef): Promise<PageState> =>
    fetchPageUrlCount(editorSdkProxy, sitemapCountApi, pageRef).catch(() => ({
      type: 'error',
    }))

  state = {
    type: 'loading',
    promise: fetchAllPageUrlCountsSafely().then(newState => {
      state = newState
    }),
  }

  return {
    reset: (pageRefs, eager) => {
      match(state)
        .with({ type: 'ready' }, async readyState => {
          if (eager) {
            const refreshPromise = fetchPageUrlCountsSafely(pageRefs)
            const currentCounts = readyState.dynamicPageUrlCounts

            state = {
              type: 'loading',
              dynamicPageUrlCounts: currentCounts,
              promise: refreshPromise.then(newState => {
                match(newState)
                  .with({ type: 'ready' }, newState => {
                    state = {
                      type: 'ready',
                      dynamicPageUrlCounts: {
                        ...currentCounts,
                        ...newState.dynamicPageUrlCounts,
                      },
                    }
                  })
                  .with({ type: 'error' }, () => {
                    state = {
                      type: 'ready',
                      dynamicPageUrlCounts: currentCounts,
                    }
                  })
                  .exhaustive()
              }),
            }
            await refreshPromise
          } else {
            pageRefs.forEach(pageRef => {
              readyState.dynamicPageUrlCounts[pageRef.id] = undefined
            })
          }
        })
        .with({ type: 'loading' }, () => {})
        .with({ type: 'error' }, () => {})
        .exhaustive()
    },

    resetAll: () => {
      match(state)
        .with({ type: 'ready' }, async () => {
          const refreshPromise = fetchAllPageUrlCountsSafely()
          state = {
            type: 'loading',
            promise: refreshPromise.then(newState => {
              state = newState
            }),
          }
          await refreshPromise
        })
        .with({ type: 'loading' }, () => {})
        .with({ type: 'error' }, () => {})
        .exhaustive()
    },

    getCount: async pageRef => {
      if (state.type === 'loading') {
        await state.promise
      }

      return match(state)
        .with({ type: 'ready' }, async readyState => {
          const pageState = await match(
            readyState.dynamicPageUrlCounts[pageRef.id],
          )
            .with(P.nullish, { type: 'error' }, () =>
              fetchPageUrlCountSafely(pageRef),
            )
            .otherwise(p => p)

          readyState.dynamicPageUrlCounts[pageRef.id] = pageState

          return getCountFromPageState(pageState)
        })
        .with({ type: 'error' }, async () => {
          const pageState = await fetchPageUrlCountSafely(pageRef)
          state = {
            type: 'ready',
            dynamicPageUrlCounts: {
              [pageRef.id]: pageState,
            },
          }
          return getCountFromPageState(pageState)
        })
        .otherwise(() => null)
    },

    getAllCounts: async () => {
      if (state.type === 'loading') {
        await state.promise
      }

      return match(state)
        .with({ type: 'ready' }, async readyState => {
          return mapValues(
            readyState.dynamicPageUrlCounts,
            getCountFromPageState,
          )
        })
        .otherwise(() => ({}))
    },
  }
}
